import React, { useState } from 'react';
import { DocumentUpload } from './DocumentUpload';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Upload, X, Settings, FileText, Image, Database } from 'lucide-react';
import { SpecificationDocument } from '../../../types';
import { AddSpecification, getPreSignedUrlToUpload } from '../../../../../redux/Project/specificationSaga';
import customNotification from '../../../../../components/ui/custom-notification';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Label } from '../../../../../components/ui/label';
import { Badge } from '../../../../../components/ui/badge';

interface DocumentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (files: FileList | null) => void;
    title?: string;
    acceptedTypes?: string;
    multiple?: boolean;
    maxFiles?: number;
    buttonText?: string;
    label?: string;
    description?: string;
    required?: boolean;
    spec?: SpecificationDocument;
    enableNDTValidation?: boolean;
    defaultNDTTechnique?: 'RT' | 'UT' | 'MT' | 'PT' | 'ET' | 'ALL';
    documentTypeDisplay?: string;
    isUpdate?: boolean;
    onUpdate?: (updatedSpec: SpecificationDocument) => void;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
    isOpen,
    onClose,
    onUpload, 
    title = 'Upload Documents',
    acceptedTypes = '.pdf,.doc,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.tiff,.tif,.bmp,.dcm,.utf,.rdt,.dta,.edd,.ect',
    multiple = false,
    maxFiles = 1,
    buttonText = 'Upload Documents',
    label = 'File',
    description = 'Upload the specification documents (PDF, Excel, DOC, Images, NDT files, etc...)',
    required = true,
    spec,
    enableNDTValidation = false,
    defaultNDTTechnique = 'ALL',
    documentTypeDisplay = 'Documents',
    isUpdate = false,
    onUpdate

}) => {
    
    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedNDTTechnique, setSelectedNDTTechnique] = useState<'RT' | 'UT' | 'MT' | 'PT' | 'ET' | 'ALL'>(defaultNDTTechnique);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [selectedTags, setSelectedTags] = useState<{ [fileIndex: number]: string[] }>({});
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // NDT technique descriptions
    const ndtTechniques = {
        RT: 'Radiographic Testing - X-ray images, DICOM files',
        UT: 'Ultrasonic Testing - UT data files, CSV reports',
        MT: 'Magnetic Particle Testing - Inspection images',
        PT: 'Dye Penetrant Testing - Inspection images',
        ET: 'Eddy Current Testing - ET data files',
        ALL: 'All NDT techniques accepted'
    };


    // File type categories for better organization
    const fileCategories = {
        documents: ['.pdf', '.doc', '.docx', '.xlsx', '.xls', '.csv'],
        images: ['.jpg', '.jpeg', '.png', '.tiff', '.tif', '.bmp'],
        ndt: ['.dcm', '.utf', '.rdt', '.dta', '.edd', '.ect']
    };

    const handleFileSelect = (files: FileList | null) => {
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setFiles(fileArray);
        }
    };

    const handleTagsChange = (fileIndex: number, tags: string[]) => {
        setSelectedTags(prev => ({
            ...prev,
            [fileIndex]: tags
        }));
    };
   
    const handleUpload = async () => {
        if (files && spec) {
            setIsUploading(true);
            try {
                const payload = {
                    pipeId: Number(spec.pipeId),
                    projectId: Number(spec.projectId),
                    projectTypeId: Number(spec.projectTypeId),
                    type: spec.type
                }
                const s3Response = await getPreSignedUrlToUpload(files, payload, spec.version ? spec.version + 1 : 1);
                if (s3Response && s3Response.length > 0) {
                    const updatedDoc = {
                        ...spec,
                        files: [
                         
                            ...s3Response.map((file: any, index: number) => ({
                                fileName: file.fileName,
                                s3Key: file.s3Key,
                                docRef: spec.docRefId || '',
                                tags: selectedTags[index] || []
                            }))
                        ],
                        docRefId:  spec.files?.[0]?.docRefId || null,
                        details: spec.files?.[0]?.details || {},
                        uploadDate: new Date().toISOString(),
                        uploadedBy: user.email || 'no email',
                        version: spec.version ? spec.version + 1 : 1
                    };


                  //  console.log('updatedDoc', updatedDoc);

                    // // Use AddSpecification for both create and update operations
                    // // The isEdit parameter determines if it's an update operation
                    const response = await AddSpecification([updatedDoc], isUpdate);
                    
                    if (response && onUpdate && isUpdate) {
                        onUpdate(response);
                    }

                    if (response) {
                        const action = isUpdate ? 'updated' : 'uploaded';
                        customNotification.success(
                            `Specification ${action} successfully`, 
                            `The ${documentTypeDisplay} specification has been ${action} successfully.`
                        );
                        onUpload(null);
                        setFiles([]);
                        setSelectedTags({});
                        setIsUploading(false);
                        onClose();
                    }
                }
            } catch (error) {
                console.error('Upload error:', error);
                customNotification.error('Upload failed', 'Failed to upload documents. Please try again.');
                setIsUploading(false);
            }
        }   
    };

    const handleClose = () => {
        setFiles([]);
        setSelectedTags({});
        setIsUploading(false);
        onClose();
    };

    const getFileTypeIcon = (fileName: string) => {
        const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
        if (fileCategories.documents.includes(ext)) return <FileText className="h-4 w-4" />;
        if (fileCategories.images.includes(ext)) return <Image className="h-4 w-4" />;
        if (fileCategories.ndt.includes(ext)) return <Database className="h-4 w-4" />;
        return <FileText className="h-4 w-4" />;
    };

    const getFileTypeBadge = (fileName: string) => {
        const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
        if (fileCategories.documents.includes(ext)) return <Badge variant="outline" className="text-xs">Document</Badge>;
        if (fileCategories.images.includes(ext)) return <Badge variant="outline" className="text-xs text-blueprint-blue bg-[rgba(0,39,63,0.06)]">Image</Badge>;
        if (fileCategories.ndt.includes(ext)) return <Badge variant="outline" className="text-xs text-blueprint-blue bg-[rgba(0,39,63,0.06)]">NDT</Badge>;
        return <Badge variant="outline" className="text-xs">Unknown</Badge>;
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-6xl sm:max-h-[100vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Upload className="h-5 w-5" />
                        {isUpdate ? `Update ${title}` : title}
                    </DialogTitle>
                </DialogHeader>

                <div className="py-8 space-y-4">
                   

                    {/* Document Upload Component */}
                    <DocumentUpload
                        onUpload={handleFileSelect}
                        acceptedTypes={acceptedTypes}
                        multiple={multiple}
                        maxFiles={maxFiles}
                        buttonText={buttonText}
                        label={label}
                        description={description}
                        required={required}
                        enableNDTValidation={enableNDTValidation}
                        ndtTechnique={selectedNDTTechnique}
                        className="w-full"
                        enableTagging={true}
                        specificationType={spec?.type}
                        projectId={spec?.projectId}
                        onTagsChange={handleTagsChange}
                        selectedTags={selectedTags}
                    />

                   
                </div>

                <DialogFooter className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        className="flex items-center gap-2"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={!files || files.length === 0 || isUploading}
                        loading={isUploading}
                        loadingText={isUpdate ? "Updating..." : "Uploading..."}
                        className="flex items-center gap-2"
                    >
                        <Upload className="h-4 w-4" />
                        {isUpdate ? "Update" : "Upload"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}; 